import moment from 'moment';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PlusOutlined, MinusOutlined, EyeOutlined, DownOutlined } from '@ant-design/icons';
import PayrollEmployeeDetailModal from '../PayrollEmployeeDetailModal';
import { Dropdown, Menu } from 'antd';
import { Modal, Button } from 'antd';

import ITVTable from 'components/ITVTable';
import PayrollCostByDepartmentFilters from './PayrollCostByDepartmentFilters';
import payrollDashboardApi from 'api/payrollDashboardApi';
import employeeApi from 'api/employeeApi';
import { formatCurrency } from 'features/insurances/constants/common';
import AvatarBig from 'components/AvatarBig';

const StyledContainer = styled.div`
  padding: 16px 24px;
  background-color: var(--cl_white);
  border-radius: 12px;
  border: 0.5px solid var(--cl_gray200);
  height: 100%;
`;

const StyledHeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-family: Roboto;
  line-height: 20px;
  color: #121926;
`;

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  .anticon {
    font-size: 12px;
    color: #1890ff;
  }
`;

const EmployeeDetailsContainer = styled.div`
  padding: 16px;
  background-color: #fafafa;
  border-top: 1px solid #e8e8e8;
`;

const EmployeeCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmployeeRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const EmployeeField = styled.div`
  flex: 1;
  padding: 0 8px;
  font-size: 14px;
  color: #262626;

  &.id {
    flex: 0 0 60px;
    text-align: center;
    font-weight: 500;
  }

  &.employee {
    flex: 2;
    font-weight: 500;
  }

  &.line {
    flex: 1.5;
  }

  &.position {
    flex: 1.5;
  }

  &.action {
    flex: 0 0 40px;
    text-align: center;
  }
`;

const EmployeeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 2px solid #e8e8e8;
  background-color: #fafafa;
  margin: -12px -12px 8px -12px;
  padding: 12px;
`;

const HeaderField = styled.div`
  flex: 1;
  padding: 0 8px;
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 600;
  text-transform: uppercase;

  &.id {
    flex: 0 0 60px;
    text-align: center;
  }

  &.employee {
    flex: 2;
  }

  &.line {
    flex: 1.5;
  }

  &.position {
    flex: 1.5;
  }

  &.action {
    flex: 0 0 40px;
    text-align: center;
  }
`;

const EmployeeActionButton = styled.button`
  background: none;
  border: 1px solid #d9d9d9;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;

  &:hover {
    background-color: #f5f5f5;
    border-color: #1890ff;
  }

  .anticon {
    font-size: 12px;
    color: #1890ff;
  }
`;

export function PayrollCostByDepartment({ location, department }: any) {
  const { t } = useTranslation(['dashboard']);

  const [isLoading, setIsLoading] = useState(false);
  const [filterMonthYear, setFilterMonthYear] = useState({
    monthYear: moment(new Date()).clone().subtract(1, 'months').format('YYYY-MM'),
  });
  const [filterLocation, setFilterLocation] = useState(
    location && location[location?.length - 1]?.id,
  );
  const [filterDepartment, setFilterDepartment] = useState(
    department && department[department?.length - 1]?.projectCode,
  );

  const [sizePage, setSizePage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPayrollCostByDepartment, setDataPayrollCostByDepartment] = useState<any>([]);
  const [employeeData, setEmployeeData] = useState<Record<string, any[]>>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedLineData, setSelectedLineData] = useState<any>(null);
  const [modalEmployeeData, setModalEmployeeData] = useState<any>([]);
  const [modalEmployeeLoading, setModalEmployeeLoading] = useState(false);
  const [isEmployeeDetailModalVisible, setIsEmployeeDetailModalVisible] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | number | null>(null);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState<any>(null);
  const [employeeCounts, setEmployeeCounts] = useState<Record<string, number>>({});

  const handleSubmit = (values: any) => {
    if (values?.from === 'monthYear') {
      const formattedMonthYear = moment(values.monthYear).format('YYYY-MM');
      setFilterMonthYear({
        monthYear: formattedMonthYear,
      });
    }
    if (values.viewByLocation !== filterLocation) {
      setFilterLocation(values?.viewByLocation);
    }
    if (values.viewByDepartment !== filterDepartment) {
      setFilterDepartment(values?.viewByDepartment);
    }
  };

  // Function to fetch employee data for a department (used by eye button)
  const fetchEmployeeDataForDepartment = async (departmentRecord: any) => {
    try {
      const recordKey =
        departmentRecord.projectPrimaryName || `row_${departmentRecord.projectPrimary}`;

      // Check if we have listEmpOfPayroll data available in the department record
      if (departmentRecord.listEmpOfPayroll && departmentRecord.listEmpOfPayroll.length > 0) {
        // Transform the listEmpOfPayroll data to match our component structure
        const employees = departmentRecord.listEmpOfPayroll.map((emp: any, index: number) => ({
          id: emp.id,
          employeeId: emp.code || `EMP${String(emp.id).padStart(3, '0')}`,
          name: emp.name || 'Unknown Employee',
          firstName: emp.name?.split(' ')[0] || '',
          lastName: emp.name?.split(' ').slice(1).join(' ') || '',
          email: emp.email || `${emp.name?.toLowerCase().replace(' ', '.')}@company.com`,
          position: emp.position || 'Employee',
          grossSalary: emp.salary || 0,
          actualTakeHome: emp.salary * 0.85 || 0, // Estimate if not provided
          totalCost: emp.salary || 0,
          avatar: emp.avatar,
        }));

        setEmployeeData((prev: any) => ({
          ...prev,
          [recordKey]: employees,
        }));

        return employees;
      } else {
        // Fallback: Fetch employees from API if listEmpOfPayroll is not available
        const response = await employeeApi.getEmployeeList({
          status: 'approved',
        });

        if (response?.data?.result) {
          // Filter employees by both projectPrimary AND projectPrimaryName matching payroll data
          const filteredEmployees = response.data.result.filter(
            (emp: any) =>
              emp.projectPrimary === departmentRecord.projectPrimary &&
              emp.projectPrimaryName === departmentRecord.projectPrimaryName,
          );

          // Transform filtered API data to match our component structure
          const employees = filteredEmployees.map((employee: any, index: number) => ({
            id: employee.id,
            employeeId: employee.employeeId || `EMP${String(employee.id).padStart(3, '0')}`,
            name:
              `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
              employee.fullName ||
              'Unknown Employee',
            firstName: employee.firstName || employee.fullName?.split(' ')[0] || '',
            lastName: employee.lastName || employee.fullName?.split(' ').slice(1).join(' ') || '',
            email:
              employee.email ||
              employee.workEmail ||
              `${employee.firstName?.toLowerCase() || 'employee'}.${
                employee.lastName?.toLowerCase() || index
              }@company.com`,
            position: employee.position || employee.designation || 'Employee',
            grossSalary: employee.grossSalary || employee.basicSalary || 0,
            actualTakeHome: employee.actualTakeHome || employee.netSalary || 0,
            totalCost: employee.totalCost || employee.grossSalary || employee.basicSalary || 0,
            avatar: employee.avatar || employee.profilePicture,
          }));

          setEmployeeData((prev: any) => ({
            ...prev,
            [recordKey]: employees,
          }));

          return employees;
        } else {
          // Fallback: empty array if no employees found
          setEmployeeData((prev: any) => ({
            ...prev,
            [recordKey]: [],
          }));
          return [];
        }
      }
    } catch (error) {
      // Fallback: use mock data or empty array on error
      return [];
    }
  };

  // Employee detail view is now handled directly through the modal
  // No need for toggle expansion functionality

  const handleViewLineDetails = async (record: any) => {
    setSelectedLineData(record);
    setIsModalVisible(true);
    setModalEmployeeLoading(true);

    try {
      // Check if we have listEmpOfPayroll data available
      if (record.listEmpOfPayroll && record.listEmpOfPayroll.length > 0) {
        // Filter employees by projectDetailId if available
        const filteredEmployees = record.projectDetailId
          ? record.listEmpOfPayroll.filter(
              (emp: any) => emp.projectDetailId === record.projectDetailId,
            )
          : record.listEmpOfPayroll;

        // Process each employee to fetch additional details by userId
        const employeeDetailsPromises = filteredEmployees.map(
          async (employee: any, index: number) => {
            try {
              // If userId is available, fetch employee details
              if (employee.userId) {
                const response = await employeeApi.getEmployeeDetail(employee.userId);

                if (response?.data) {
                  // Merge employee details with payroll data
                  return {
                    id: employee.userId,
                    userId: employee.userId,
                    employeeId: response.data.employeeId || `EMP${String(index).padStart(3, '0')}`,
                    name: response.data.fullName || 'Unknown Employee',
                    firstName:
                      response.data.firstName || response.data.fullName?.split(' ')[0] || '',
                    lastName:
                      response.data.lastName ||
                      response.data.fullName?.split(' ').slice(1).join(' ') ||
                      '',
                    email:
                      response.data.email ||
                      response.data.workEmail ||
                      `employee${index}@company.com`,
                    position: response.data.position?.name || response.data.roleName || 'Employee',
                    avatar: response.data.avatar || response.data.profilePicture,
                    // Payroll specific data from listEmpOfPayroll
                    totalSalaryCost: employee.totalSalaryCost || 0,
                    actualReceiveSalary: employee.actualReceiveSalary || 0,
                    insurance: employee.insurance || 0,
                    totalGrossSalary: employee.totalGrossSalary || 0,
                    overTime: employee.overTime || 0,
                    tax: employee.tax || 0,
                  };
                }
              }

              // If API call fails or userId not available, use the data we have
              return {
                id: employee.userId || `emp-${index}`,
                userId: employee.userId,
                employeeId: `EMP${String(index).padStart(3, '0')}`,
                name: 'Employee ' + (index + 1),
                firstName: '',
                lastName: '',
                email: `employee${index}@company.com`,
                position: 'Employee',
                // Payroll specific data from listEmpOfPayroll
                totalSalaryCost: employee.totalSalaryCost || 0,
                actualReceiveSalary: employee.actualReceiveSalary || 0,
                insurance: employee.insurance || 0,
                totalGrossSalary: employee.totalGrossSalary || 0,
                overTime: employee.overTime || 0,
                tax: employee.tax || 0,
              };
            } catch (error) {
              // Return basic data if API call fails
              return {
                id: employee.userId || `emp-${index}`,
                userId: employee.userId,
                employeeId: `EMP${String(index).padStart(3, '0')}`,
                name: 'Employee ' + (index + 1),
                firstName: '',
                lastName: '',
                email: `employee${index}@company.com`,
                position: 'Employee',
                // Payroll specific data from listEmpOfPayroll
                totalSalaryCost: employee.totalSalaryCost || 0,
                actualReceiveSalary: employee.actualReceiveSalary || 0,
                insurance: employee.insurance || 0,
                totalGrossSalary: employee.totalGrossSalary || 0,
                overTime: employee.overTime || 0,
                tax: employee.tax || 0,
              };
            }
          },
        );

        // Wait for all employee detail requests to complete
        const employees = await Promise.all(employeeDetailsPromises);

        setModalEmployeeData(employees);
      } else {
        // Fallback to API call if listEmpOfPayroll is not available
        const { data: response } = await employeeApi.getEmployeeList({ status: 'approved' });

        if (response?.result) {
          const filteredEmployees = response.result.filter(
            (emp: any) =>
              emp.projectPrimary === record.projectPrimary &&
              emp.projectPrimaryName === record.projectPrimaryName &&
              emp.projectDetailId === record.projectDetailId,
          );

          const employees = filteredEmployees.map((employee: any, index: number) => ({
            id: employee.id,
            userId: employee.id,
            employeeId: employee.employeeId || `EMP${String(employee.id).padStart(3, '0')}`,
            name:
              `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
              employee.fullName ||
              'Unknown Employee',
            firstName: employee.firstName || employee.fullName?.split(' ')[0] || '',
            lastName: employee.lastName || employee.fullName?.split(' ').slice(1).join(' ') || '',
            email:
              employee.email ||
              employee.workEmail ||
              `${employee.firstName?.toLowerCase() || 'employee'}.${
                employee.lastName?.toLowerCase() || index
              }@company.com`,
            position: employee.position || employee.designation || 'Employee',
            // Default payroll values since we don't have actual data
            totalSalaryCost: employee.salary || 0,
            actualReceiveSalary: (employee.salary || 0) * 0.85, // Estimate
            insurance: 0,
            totalGrossSalary: employee.salary || 0,
            overTime: 0,
            tax: (employee.salary || 0) * 0.15, // Estimate
          }));

          setModalEmployeeData(employees);
        } else {
          setModalEmployeeData([]);
        }
      }
    } catch (error) {
      setModalEmployeeData([]);
    } finally {
      setModalEmployeeLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedLineData(null);
    setModalEmployeeData([]);
    setModalEmployeeLoading(false);
  };

  const handleViewEmployeeDetail = (employeeId: string | number, employeeData?: any) => {
    setSelectedEmployeeId(employeeId);

    // If employee data is provided directly, use it
    if (employeeData) {
      setSelectedEmployeeData(employeeData);
    } else {
      // Otherwise, try to find employee data from modalEmployeeData or expanded rows
      const foundEmployee = modalEmployeeData.find((emp: any) => emp.id === employeeId);

      if (foundEmployee) {
        setSelectedEmployeeData(foundEmployee);
      } else {
        // Search in all expanded departments
        let foundInExpanded = null;

        // Check each department's employee data
        Object.keys(employeeData).forEach((deptKey) => {
          const deptEmployees = employeeData[deptKey] || [];
          const found = deptEmployees.find((emp: any) => emp.id === employeeId);

          if (found) {
            foundInExpanded = found;
          }
        });

        if (foundInExpanded) {
          setSelectedEmployeeData(foundInExpanded);
        } else {
          setSelectedEmployeeData(null);
        }
      }
    }

    setIsEmployeeDetailModalVisible(true);
  };

  const handleCloseEmployeeDetailModal = () => {
    setIsEmployeeDetailModalVisible(false);
    setSelectedEmployeeData(null);
  };

  const renderEmployeeDetails = (record: any) => {
    const recordKey = record.projectPrimaryName || record.key;
    const employees = employeeData[recordKey] || [];

    return (
      <EmployeeDetailsContainer>
        <EmployeeCard>
          <EmployeeHeader>
            <HeaderField className="id">ID</HeaderField>
            <HeaderField className="employee">Employee</HeaderField>
            <HeaderField className="line">Line</HeaderField>
            <HeaderField className="position">Position</HeaderField>
            <HeaderField className="action">Action</HeaderField>
          </EmployeeHeader>
          {employees.map((employee: any) => {
            return (
              <div key={employee.employeeId || employee.id}>
                <EmployeeRow>
                  <EmployeeField className="id">{employee.employeeId || employee.id}</EmployeeField>
                  <EmployeeField className="employee">{employee.name}</EmployeeField>
                  <EmployeeField className="line">{record.projectPrimaryName}</EmployeeField>
                  <EmployeeField className="position">{employee.position}</EmployeeField>
                  <EmployeeField className="action">
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <EmployeeActionButton
                        onClick={() => handleViewEmployeeDetail(employee.id, employee)}
                      >
                        <EyeOutlined />
                      </EmployeeActionButton>
                    </div>
                  </EmployeeField>
                </EmployeeRow>
              </div>
            );
          })}
        </EmployeeCard>
      </EmployeeDetailsContainer>
    );
  };

  let COLUMNS = [
    {
      title: 'ID',
      width: 60,
      minWidth: 60,
      dataIndex: 'index',
      key: 'index',
      align: 'center',
      render: (text: any, record: any, index: any) => {
        if (record.isEmployeeRow) {
          return record.employee.employeeId || record.employee.id;
        }
        if (record.isEmployeeDetailsRow) {
          return null;
        }
        if (record.isMainRow) {
          return index + 1;
        }
        return index + 1;
      },
    },
    {
      title: 'Line',
      width: 200,
      minWidth: 200,
      dataIndex: 'projectPrimaryName',
      key: 'projectPrimaryName',
      align: 'left',
      render: (text: any, record: any, index: any) => {
        // Only show for main department rows
        if (!record.isMainRow) {
          return null;
        }

        // Main department row
        return <div style={{ display: 'flex', alignItems: 'center' }}>{text}</div>;
      },
    },
    {
      title: 'Number of Employees',
      width: 150,
      minWidth: 150,
      dataIndex: 'totalEmpolyee',
      key: 'totalEmpolyee',
      align: 'center',
      render: (text: any, record: any, index: any) => {
        // Only show for main department rows
        if (!record.isMainRow) {
          return null;
        }

        return text;
      },
    },
    {
      title: 'Gross Salary',
      width: 150,
      minWidth: 150,
      dataIndex: 'totalGrossSalary',
      key: 'totalGrossSalary',
      align: 'right',
      render: (text: any, record: any, index: any) => {
        // Only show for main department rows
        if (!record.isMainRow) {
          return null;
        }

        return text && formatCurrency(parseFloat(text).toFixed(2));
      },
    },
    {
      title: 'Actual take home',
      width: 150,
      minWidth: 150,
      dataIndex: 'actualReceiveSalary',
      key: 'actualReceiveSalary',
      align: 'right',
      render: (text: any, record: any, index: any) => {
        // Only show for main department rows
        if (!record.isMainRow) {
          return null;
        }

        return text && formatCurrency(parseFloat(text).toFixed(2));
      },
    },
    {
      title: 'Total Cost',
      width: 150,
      minWidth: 150,
      dataIndex: 'totalSalaryCost',
      key: 'totalSalaryCost',
      align: 'right',
      render: (text: any, record: any, index: any) => {
        return text && formatCurrency(parseFloat(text).toFixed(2));
      },
    },
    {
      title: 'Action',
      width: 80,
      minWidth: 80,
      key: 'action',
      align: 'center',
      render: (text: any, record: any, index: any) => {
        // Only show for main department rows
        if (!record.isMainRow) {
          return null;
        }

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              padding: '8px 0', // Add some vertical padding
            }}
          >
            <button
              onClick={() => handleViewLineDetails(record)}
              style={{
                background: 'none',
                border: '1px solid #f0f0f0',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
                width: '32px',
                height: '28px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e6f7ff';
                e.currentTarget.style.borderColor = '#1890ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#f0f0f0';
              }}
            >
              <EyeOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
            </button>
          </div>
        );
      },
    },
  ];

  // Transform data to only include main rows (no expanded rows)
  const getTransformedData = () => {
    const transformedData: any[] = [];

    dataPayrollCostByDepartment.forEach((item: any, index: number) => {
      // Add only the main row
      transformedData.push({
        ...item,
        key: item.projectPrimaryName || `row_${index}`,
        isMainRow: true,
      });
    });

    return transformedData;
  };

  useEffect(() => {
    const fetchDataPayrollDepartment = async () => {
      setIsLoading(true);
      const params = {
        period: filterMonthYear.monthYear,
        locationId: filterLocation,
        projectPrimary: filterDepartment,
      };
      const response = await payrollDashboardApi.getPayrollLine(params);

      // Process the response data to ensure listEmpOfPayroll is available
      const processedData =
        response?.data?.result?.map((item: any) => {
          // If listEmpOfPayroll doesn't exist, initialize it
          if (!item.listEmpOfPayroll) {
            item.listEmpOfPayroll = [];
          }

          // Pre-load employee data for each department to avoid additional API calls
          if (item.listEmpOfPayroll.length > 0) {
            // Transform the listEmpOfPayroll data to match our component structure
            const employeeKey = item.projectPrimaryName || `row_${item.projectPrimary}`;

            // Map the employee data to our expected format
            const mappedEmployees = item.listEmpOfPayroll.map((emp: any) => ({
              id: emp.id,
              employeeId: emp.code || `EMP${String(emp.id).padStart(3, '0')}`,
              name: emp.name || 'Unknown Employee',
              firstName: emp.name?.split(' ')[0] || '',
              lastName: emp.name?.split(' ').slice(1).join(' ') || '',
              email: emp.email || `${emp.name?.toLowerCase().replace(' ', '.')}@company.com`,
              position: emp.position || 'Employee',
              grossSalary: emp.salary || 0,
              actualTakeHome: emp.salary * 0.85 || 0, // Estimate if not provided
              totalCost: emp.salary || 0,
            }));

            // Store the mapped employees in our employeeData state
            setEmployeeData((prevData) => ({
              ...prevData,
              [employeeKey]: mappedEmployees,
            }));
          }

          return item;
        }) || [];

      setDataPayrollCostByDepartment(processedData);
      setIsLoading(false);
    };
    fetchDataPayrollDepartment().catch((err) => {
      setIsLoading(false);
    });
  }, [filterMonthYear, filterLocation, filterDepartment]);

  return (
    <StyledContainer>
      <StyledWrapper>
        <StyledHeaderTitle>Payroll Cost by Line</StyledHeaderTitle>
        <PayrollCostByDepartmentFilters
          initialValues={{
            monthYear: moment(new Date(filterMonthYear.monthYear)),
            viewByLocation: filterLocation,
            viewByDepartment: filterDepartment,
          }}
          loading={isLoading}
          onSubmit={handleSubmit}
          location={location}
          department={department}
        />
      </StyledWrapper>

      <ITVTable
        // loading={loading}
        columns={COLUMNS}
        data={getTransformedData()}
        isRowSelect={false}
        setOffset={setCurrentPage}
        offset={currentPage}
        setLimit={setSizePage}
        limit={sizePage}
        height={400}
        scroll={{ x: 100, y: 100 }}
        loading={isLoading}
        rowClassName={(record: any) => {
          if (record.isEmployeeRow) return 'employee-row';
          if (record.isEmployeeDetailsRow) return 'employee-details-row';
          return 'main-row';
        }}
      />

      <Modal
        title={`Line ${selectedLineData?.projectPrimaryName || ''}`}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={1150}
        centered
        destroyOnClose
      >
        {selectedLineData && (
          <div>
            {/* Payroll Attributes */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
                Payroll Details
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div>
                  <span style={{ fontWeight: '500', color: '#8c8c8c' }}>actualReceiveSalary: </span>
                  <span>
                    {selectedLineData.actualReceiveSalary
                      ? formatCurrency(parseFloat(selectedLineData.actualReceiveSalary).toFixed(2))
                      : 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#8c8c8c' }}>projectPrimary: </span>
                  <span>{selectedLineData.projectPrimary || 'N/A'}</span>
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#8c8c8c' }}>projectPrimaryName: </span>
                  <span>{selectedLineData.projectPrimaryName || 'N/A'}</span>
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#8c8c8c' }}>tax: </span>
                  <span>
                    {selectedLineData.tax
                      ? formatCurrency(parseFloat(selectedLineData.tax).toFixed(2))
                      : 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#8c8c8c' }}>totalEmpolyee: </span>
                  <span>{selectedLineData.totalEmpolyee || 'N/A'}</span>
                </div>
                <div>
                  <span style={{ fontWeight: '500', color: '#8c8c8c' }}>totalGrossSalary: </span>
                  <span>
                    {selectedLineData.totalGrossSalary
                      ? formatCurrency(parseFloat(selectedLineData.totalGrossSalary).toFixed(2))
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Employee Table */}
            <div>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
                Employees
              </h3>
              <ITVTable
                columns={[
                  {
                    title: 'ID',
                    dataIndex: 'employeeId',
                    key: 'employeeId',
                    width: 80,
                    minWidth: 80,
                    align: 'center',
                  },
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                    width: 200,
                    minWidth: 150,
                    flex: 1,
                    render: (text: any, record: any) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 0',
                          minHeight: '48px',
                        }}
                      >
                        {/* Employee Avatar */}
                        <div style={{ marginRight: '12px', display: 'flex', alignItems: 'center' }}>
                          <AvatarBig
                            size={32}
                            fontSize={'12px'}
                            firstName={record.firstName || record.name?.split(' ')[0] || ''}
                            lastName={
                              record.lastName || record.name?.split(' ').slice(1).join(' ') || ''
                            }
                            url={record.avatar}
                          />
                        </div>

                        {/* Employee Name and Email */}
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minHeight: '32px',
                            overflow: 'visible',
                            paddingRight: '8px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#262626',
                              marginBottom: '2px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {record.name}
                          </div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#8c8c8c',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {record.email ||
                              `${record.name.toLowerCase().replace(' ', '.')}@company.com`}
                          </div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Total Salary Cost',
                    dataIndex: 'totalSalaryCost',
                    key: 'totalSalaryCost',
                    width: 140,
                    minWidth: 140,
                    align: 'right',
                    render: (text: any) => text && formatCurrency(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: 'Actual Take Home',
                    dataIndex: 'actualReceiveSalary',
                    key: 'actualReceiveSalary',
                    width: 140,
                    minWidth: 140,
                    align: 'right',
                    render: (text: any) => text && formatCurrency(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: 'Insurance',
                    dataIndex: 'insurance',
                    key: 'insurance',
                    width: 120,
                    minWidth: 120,
                    align: 'right',
                    render: (text: any) => text && formatCurrency(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: 'Gross Salary',
                    dataIndex: 'totalGrossSalary',
                    key: 'totalGrossSalary',
                    width: 140,
                    minWidth: 140,
                    align: 'right',
                    render: (text: any) => text && formatCurrency(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: 'Overtime',
                    dataIndex: 'overTime',
                    key: 'overTime',
                    width: 120,
                    minWidth: 120,
                    align: 'right',
                    render: (text: any) => text && formatCurrency(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: 'Tax',
                    dataIndex: 'tax',
                    key: 'tax',
                    width: 120,
                    minWidth: 120,
                    align: 'right',
                    render: (text: any) => text && formatCurrency(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    width: 90,
                    minWidth: 90,
                    align: 'center',
                    render: (_, record) => {
                      const menuItems = [
                        {
                          key: 'viewProfile',
                          icon: <EyeOutlined style={{ color: '#1890ff' }} />,
                          label: 'View Profile',
                          onClick: () => handleViewEmployeeDetail(record.id, record),
                        },
                      ];

                      return (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Dropdown
                            menu={{ items: menuItems }}
                            trigger={['click']}
                            placement="bottomCenter"
                          >
                            <button
                              style={{
                                background: '#f5f5f5',
                                border: '1px solid #d9d9d9',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '28px',
                                transition: 'all 0.3s',
                                boxShadow: '0 2px 0 rgba(0,0,0,0.015)',
                                ':hover': {
                                  background: '#e6f7ff',
                                  borderColor: '#1890ff',
                                },
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '16px',
                                  lineHeight: 1,
                                  color: '#1890ff',
                                  fontWeight: 'bold',
                                }}
                              >
                                •••
                              </span>
                            </button>
                          </Dropdown>
                        </div>
                      );
                    },
                  },
                ]}
                data={modalEmployeeData}
                isRowSelect={false}
                pagination={false}
                height={300}
                loading={modalEmployeeLoading}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Employee Detail Modal */}
      {selectedEmployeeId && (
        <PayrollEmployeeDetailModal
          isVisible={isEmployeeDetailModalVisible}
          employeeId={selectedEmployeeId || ''}
          onClose={handleCloseEmployeeDetailModal}
          preloadedEmployeeData={selectedEmployeeData}
        />
      )}
    </StyledContainer>
  );
}
